// ts ignore file, as it came from js and needs to be refactored
// @ts-nocheck
import { UserManager } from "oidc-client-ts";
import { config } from '@/config/env';
import { find } from "lodash";
import { clearAccessToken, getAccessToken, setAccessToken } from "@/features/auth/storage";
import { clearTenancySelectionRequest } from "@/features/tenancy/recovery";
import { clearTenancyContext } from "@/features/tenancy/storage";

const FORCE_TOKEN_REFRESH = false && import.meta.env.DEV;

const override = {};
const checkStatus = function checkStatus(res) {
  if (res.ok) {
    return res;
  }
  throw res;
};

const clearAuthenticationState = () => {
  clearAccessToken();
  clearTenancyContext();
  clearTenancySelectionRequest();
};

/**
 * Maps an access token to a promise that is resolved when the token is refreshed
 * This is to prevent multiple requests from trying to refresh the token at the same time
 */
const refreshPromises = {};

const decodeJwtPayload = (token) => {
  const parts = token?.split(".");
  if (parts?.length !== 3 || !parts[1]) {
    throw new Error("Invalid JWT");
  }

  const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const paddedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
  const binary = atob(paddedBase64);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));

  return JSON.parse(new TextDecoder().decode(bytes));
};

class DefaultProvider {
  constructor(options) {
    this.options = options;
    this.mgr = new UserManager({
      client_id: options.client_id,
      authority: options.endpoint,
      redirect_uri: `${location.protocol}//${location.host}/authcallback`,
      response_type: "code"
    });
  }

  handler(type, params) {
    let accessToken = getAccessToken();
    switch (type) {
      case "signIn":
        return new Promise((resolve, reject) => {
          console.log("[OIDC] Redirecting to signin");
          let params = {};
          console.log('setting', this.mgr.settings);
          this.mgr
            .signinRedirect()
            .then((response) => {
              // Tokens are returned via authCallback below
              return resolve({ authenticating: true });
            })
            .catch((error) => {
              console.log("Error redirecting for sign in");
              console.error(error);
              resolve({ authenticated: false });
            });
        });
      case "silentAuthCheck":
        if (!accessToken) {
          console.log("[OIDC] No token");
          return Promise.resolve({ authenticated: false });
        }

        if (refreshPromises[accessToken]) return refreshPromises[accessToken];

        return (refreshPromises[accessToken] = new Promise(async (resolve, reject) => {
          // access/refresh token might still be valid - we perform silent re-auth
          // (params.force renews a token that hasn't expired yet, for callers that need its full lifetime)
          if (!FORCE_TOKEN_REFRESH && !params?.force && !this.tokenExpired(accessToken)) {
            let user = await this.mgr.getUser();
            await this.returnUser(resolve, reject, user);
          } else {
            // so we can validate the token is still valid from the server
            console.log("[OIDC] Silent refreshing token", this.mgr);
            this.mgr
              .signinSilent({
                extraTokenParams: {
                  redirect_uri: this.mgr.settings.redirect_uri,
                  ...(this.mgr.settings.extraTokenParams || {}),
                },
              })
              .then(async (resp) => {
                if (resp) {
                  this.storeTokens(resp);
                  await this.returnUser(resolve, reject, resp);
                } else {
                  resolve({ authenticated: false });
                }
              })
              .catch(async (error) => {
                console.log("[OIDC] Failed to refresh token");
                console.error(error);
                // a forced renewal of a token that's still valid keeps the user signed in with it
                if (params?.force && !this.tokenExpired(accessToken)) {
                  await this.returnUser(resolve, reject, await this.mgr.getUser());
                  return;
                }
                clearAuthenticationState();
                // Something went wrong. Send back false and maybe they will get to sign in manually
                resolve({ authenticated: false });
              });
          }
        }).then((result) => {
          delete refreshPromises[accessToken];
          return result;
        }, (error) => {
          delete refreshPromises[accessToken];
          clearAuthenticationState();
          throw error;
        }));
      case "authCallback":
        return new Promise((resolve, reject) => {
          this.mgr
            .signinRedirectCallback()
            .then(async (resp) => {
              this.storeTokens(resp);
              await this.returnUser(resolve, reject, resp, true);
              //getUserFromIdToken(resolve, resp.id_token, resp.refresh_token, true);
            })
            .catch((err) => {
              console.log("Error in OIDC Provider", err);
              reject();
            });
        });
      case "validAuth":
        if (!accessToken || this.tokenExpired(accessToken)) return false;
        else return true;
      case "applyAuthentication":
        if (accessToken) {
          params[this.options.header_key || "Authorization"] = "Bearer " + accessToken;
        }
        return params;
      case "applySecureEndpoint":
        // TO REMOVE
        //return 'example';
        break;
      case "getUser":
        return this.user;
      case "getBackendUrl":
        if (!this.options.getBackendUrl) Promise.reject("Invalid configuration - no getBackendUrl provided in options");
        return this.options.getBackendUrl(this.user);
      case "signOut":
        console.log("[OIDC] Redirecting to signout");
        clearAuthenticationState();
        this.mgr
          .signoutRedirect({
            post_logout_redirect_uri: `${location.protocol}//${location.host}/loggedOut`,
          })
          .then((resp) => {
            // In Azure this doesn't seem to come back, so removing above
            // Though this could cause a problem if there is an error
          });
        break;
      default:
        return Promise.reject(`Unsupported Imminently Platform authClient action type ${type}`);
    }
  }

  storeTokens(response) {
    setAccessToken(response.id_token);
  }

  tokenExpired(token) {
    try {
      const payload = decodeJwtPayload(token);
      if (!payload.exp) return true;
      const exp = payload.exp * 1000;
      //console.log("[OIDC] token expiry check:", new Date(exp));
      return new Date(exp) <= new Date();
    } catch (error) {
      console.log("error checking token", error);
      return true;
    }
  }

  async returnUser(resolve, reject, response, redirect = false) {
    try {
      const token = response?.id_token;
      const parts = token?.split(".");
      if (parts?.length !== 3 || parts[2] === "") {
        clearAuthenticationState();
        return resolve({
          authenticated: false,
          redirectTo: "/loggedOut",
        });
      }
      const profile = response.profile || decodeJwtPayload(token);
      let user = this.parseProfile(profile);
      if (this.options.additionalInformationCheck) {
        // Call this URL to get additional information about the user
        let headers = {};
        if (this.options.customHeaders) headers = this.options.customHeaders(headers);

        headers[this.options.header_key || "Authorization"] = `Bearer ${response.id_token}`;
        let additional = await fetch(this.options.additionalInformationCheck, {
          headers,
        })
          .then(checkStatus)
          .then((res) => res.json());
        user = { ...user, ...additional };
      }
      console.log("[OIDC] token expires at:", new Date(response.expires_at * 1000));
      console.log("[OIDC] user roles:", {
        id: user.id,
        email: user.email,
        roles: user.roles || [],
      });
      // console.log('token expires at:', new Date(response.expires_at * 1000));
      this.user = user;
      if (this.options.onAfter) this.options.onAfter(this.user);
      resolve({
        authenticated: true,
        user: user,
        redirectTo: redirect ? this.options.authenticatedRedirectPath : null,
      });
    } catch (error) {
      console.log("error getting profile", error);
      reject(error);
    }
  }

  parseProfile(profile) {
    console.log("pro", profile);
    return {
      username: profile["user_displayname"],
      id: profile["user_id"],
      roles: ["user", "admin"],
    };
  }
}
const DefaultIdentityProvider = (options) => {
  let instance = new DefaultProvider(options);

  return instance.handler;
};

class AzureProvider extends DefaultProvider {
  constructor(options) {
    super(options);
    let authority = options.endpoint;
    //if (!options.b2c && !authority.includes("/v2.0")) authority = `${authority}/v2.0`;
    let scope;
    if (!options.b2c) {
      scope = config.AUTH_SEND_SCOPE !== false && config.AUTH_SEND_SCOPE !== "false" ? (
        config.AUTH_SCOPE_OVERRIDE ? config.AUTH_SCOPE_OVERRIDE : 'profile openid ' + options.scope + ' offline_access'
      ) : undefined;
    }

    this.mgr = new UserManager({
      client_id: options.client_id,
      authority: authority,
      redirect_uri: `${location.protocol}//${location.host}/authcallback`,
      response_type: "code",
      scope: scope
    });
  }

  parseProfile(profile) {
    let roles = profile.roles || [];
    if (config.AUTH_ROLES) {
      let role_mapping;

      try {
        role_mapping = JSON.parse(config.AUTH_ROLES);
      } catch (error) {
        console.error("error parsing role mapping", error);
      }

      roles = roles.map(role => {
        let match = find(role_mapping, { id: role });
        return match ? match.value : role;
      });
    }

    if (!roles.includes('user')) roles.push('user');
    return {
      username: profile.name,
      first_name: profile.given_name,
      last_name: profile.family_name,
      id: profile.oid || profile.sub,
      email: profile.emails ? profile.emails[0] : undefined,
      roles: roles,
      groups: (Array.isArray(profile.group_ids) ? profile.group_ids : []).map((group, index) => {
        return {
          id: group,
          name: profile.group_names?.[index]?.replace('Decisively.', '') || group,
        }
      })
    };
  }
}

const AzureIdentityProvider = (options) => {
  let instance = new AzureProvider(options);
  return Object.assign((type, params) => {
    return instance.handler(type, params);
  }, {
    instance
  });
  //return instance.handler;
};

export { AzureIdentityProvider, DefaultIdentityProvider };
